import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { Button, DesignLanguage, Grid, IconNames, Message, Text, useDesignLanguagePerClass, useIsDesignLanguage } from '@afterpaytouch/core'
import FormBuilder from '@afterpaytouch/form'
import { Country } from '@afterpaytouch/portal-api/types'
import { FormWrapper } from '../'
import { useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './style.module.css'
import {
  setCheckoutStatusToBeLoaded,
  setConsumerError,
  setLoggedInLoadingMessageKey,
  updateAccountDetails,
  updateRegistrationStatus,
  useAppDispatch,
  useConsumerSelector,
  useCountry,
  useIdentityVerifyMutation,
} from '../../state'
import { getSchemaAndFormData, VerifyRetryForm, transformToBackend, getKeysToIgnoreInCompare } from './utils'
import { compareObjects } from '../../utils/comparison'
import { unwrapResult } from '@reduxjs/toolkit'
import { ProfileErrorCode, RegistrationStatus } from '../../model'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { validAddressOption } from './validations'
import { DisabledModal } from '../DisabledModal'
import { Disclaimer } from '../Disclaimer/Disclaimer'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { isCallable } from '@afterpay/utils'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { IdentityCheckStatus, Channel } from '@afterpaytouch/portal-api/identity/types'
import { useLazyGetIdentitiesQuery } from '@afterpaytouch/portal-api/consumer/account'

interface Props {
  onSuccess?: () => void
}

export const VerifyRetry: FunctionComponent<Props> = ({ onSuccess }) => {
  const { t } = useTranslation()
  const { textSize, buttonSize, extraSmallTextSize } = useCheckoutSizes()
  const dispatch = useAppDispatch()
  const consumer = useConsumerSelector()
  const country = useCountry()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [identityVerify] = useIdentityVerifyMutation()
  const designLanguageClass = useDesignLanguagePerClass(styles)
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const [hasError, setHasError] = useState(false)
  const [hasAddressError, setHasAddressError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [getIdentities] = useLazyGetIdentitiesQuery()

  const isSameProfileInfoWarning = useMemo(() => {
    return !isUndefinedOrEmpty(consumer?.error) && consumer?.error === ProfileErrorCode.SameProfileInfoWarning
  }, [consumer?.error])

  useEffect(() => {
    if (isSameProfileInfoWarning) {
      logEvent(TrackingEvent.VIEWED_SAME_PROFILE_INFO_WARNING_ON_VERIFY_RETRY_PAGE)
    }
  }, [isSameProfileInfoWarning])

  const { schema, initialFormData } = useMemo(() => {
    const renderError = (): JSX.Element => {
      const errorCode = consumer?.error
      let errorMessage = t('verifyIdentity:error:unableToUpdateAccountDetails')
      if (errorCode === ProfileErrorCode.InvalidAddress) {
        errorMessage = t('verifyIdentity:error:invalidAddress')
      }
      return (
        <Grid>
          <Text color='Fire' size={extraSmallTextSize} testNameSpace='verify-retry-error'>
            {errorMessage}
          </Text>
        </Grid>
      )
    }

    const components = {
      profileError: () => renderError(),
      footer: () => {
        return (
          <>
            <Disclaimer />
            <div className={designLanguageClass.buttonWrapper}>
              <Button.Secondary size={buttonSize} loading={isLoading} disabled={isLoading} type='submit' padding='Fluid'>
                {t('verifyIdentity:retry:cta')}
              </Button.Secondary>
            </div>
          </>
        )
      },
    }

    return getSchemaAndFormData(consumer, components, t, country, hasAddressError)
  }, [consumer, t, buttonSize, extraSmallTextSize, country, isLoading, hasAddressError])

  const [formData, setFormData] = useState<VerifyRetryForm>(initialFormData)

  const isInvalidForm = (data: VerifyRetryForm): boolean => {
    const areFormValuesSameAsProfile = compareObjects(initialFormData, data, true, getKeysToIgnoreInCompare(country))
    if (areFormValuesSameAsProfile) {
      dispatch(setConsumerError(ProfileErrorCode.SameProfileInfoWarning))
    }
    const isValidAddress = !validAddressOption(formData?.address, country)
    setHasAddressError(isValidAddress)
    return areFormValuesSameAsProfile || hasError || isValidAddress
  }

  const handleOnChange = (data: VerifyRetryForm): void => {
    setFormData(data)
    if (hasAddressError) {
      setHasAddressError(false)
    }
    if (isSameProfileInfoWarning) {
      const areFormValuesSameAsProfile = compareObjects(initialFormData, data, true, getKeysToIgnoreInCompare(country))
      if (!areFormValuesSameAsProfile) {
        dispatch(setConsumerError(undefined))
      }
    }
  }

  const handleStateChange = (isValid: boolean): void => {
    setHasError(!isValid)
  }

  const handleSubmit = async (inputData: VerifyRetryForm): Promise<void> => {
    const data = transformToBackend(inputData)
    logEvent(TrackingEvent.PRESSED_SUBMIT_BUTTON_ON_VERIFY_RETRY_PAGE)

    if (isInvalidForm(data)) {
      return
    }

    setIsLoading(true)
    const requestFormData = {
      ...data,
      ...data.address,
      countryCode: (data?.address?.countryCode ?? country) as Country,
    }

    let currentStep = 'updateAccountDetails'

    try {
      const response = await dispatch(updateAccountDetails(requestFormData))
      unwrapResult(response)

      currentStep = 'identityVerify'

      const { identityCheckMetadata } = await getIdentities().unwrap()

      const hasProfileCheckAttemptsLeft = identityCheckMetadata?.profileCheckAttemptsLeft === true

      if (hasProfileCheckAttemptsLeft) {
        const identityVerifyResponse = await identityVerify({
          channel: Channel.CHECKOUT,
        }).unwrap()

        const { identityCheckStatus } = identityVerifyResponse

        if (identityCheckStatus === IdentityCheckStatus.VERIFIED) {
          dispatch(updateRegistrationStatus(RegistrationStatus.IDVerified))
        }
      }

      dispatch(setCheckoutStatusToBeLoaded())
      dispatch(setLoggedInLoadingMessageKey('verifyIdentity'))

      currentStep = 'onSuccess'
      if (isCallable(onSuccess)) onSuccess()
    } catch (error) {
      logEvent('DEBUG_VERIFY_RETRY_ERROR', {
        country,
        operation: currentStep,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorName: error instanceof Error ? error.name : 'Unknown error type',
        errorStack: error instanceof Error ? error.stack : undefined,
        errorDetails: JSON.stringify(error),
      })

      setIsLoading(false)
    }
  }

  const renderSameProfileInfoWarning = (): JSX.Element => {
    const iconName: IconNames = isArcadeDesignLanguage ? 'Warning' : 'WarningAlt'
    const warningHeading = t('verifyIdentity:retry:sameProfileDetailsHeading')
    const warningDescription = (
      <Grid>
        <Text size='XS'>{t('verifyIdentity:retry:sameProfileDetailsDescription')}</Text>
      </Grid>
    )

    return (
      <div className={styles.messageContainer}>
        <Message testNameSpace='verify-retry-same-profile' kind='Info' iconName={iconName} heading={warningHeading} description={warningDescription} />
      </div>
    )
  }

  useEffect(() => {
    if (typeof consumer?.error !== 'undefined') {
      window?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }
  }, [consumer?.error])

  return (
    <>
      <div className={styles.container}>
        <Grid>
          <Text size={textSize}>{t('verifyIdentity:retry:description')}</Text>
        </Grid>
        {isSameProfileInfoWarning && renderSameProfileInfoWarning()}
        <FormWrapper>
          <MaskedElement actionName='Verify Identity Form'>
            <FormBuilder
              schema={schema}
              onSubmit={handleSubmit}
              onChange={handleOnChange}
              onStateChange={handleStateChange}
              disabled={isLoading}
              testNameSpace='verify-retry'
            />
          </MaskedElement>
        </FormWrapper>
        {isLoading && <DisabledModal />}
      </div>
    </>
  )
}
